import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import AttributeCombobox from "./AttributeCombobox";

// Product attributes — the shared editor block (Sep 2026).
//
// ONE component, used by BOTH the Add-product-manually modal and the
// Edit-product modal. That shared use is the point: the two forms had
// drifted apart, which is how the confirmed defect survived (the manual
// form posted dietary + allergens inside an `extra` object that the
// create route silently dropped). A single block cannot drift.
//
// Simplicity for plain restaurants is preserved three ways:
//   * every field is inside a "Product details (optional)" disclosure
//     that starts COLLAPSED;
//   * a field only renders when its capability is switched on for THIS
//     catalog, so a catalog with the defaults shows nothing new at all;
//   * Spice Level renders OUTSIDE the disclosure for a restaurant
//     catalog, because for them it is the one field that matters.
//
// `value` is a plain object of the attribute state; `onChange(patch)`
// merges. Nothing here talks to the product API — the parent owns the
// save, so validation errors surface in the parent's own error banner.
const SPICE_OPTIONS = [
  { code: "", label: "Not set" },
  { code: "low", label: "Low", short: "L" },
  { code: "medium", label: "Medium", short: "M" },
  { code: "hot", label: "Hot", short: "H" },
];

export function SpiceLevelField({ value, onChange, testIdPrefix = "product" }) {
  return (
    <div className="space-y-1">
      <span className="block text-[11px] font-medium uppercase tracking-wider text-slate-500">
        Spice level
      </span>
      <div
        className="flex flex-wrap gap-2"
        role="radiogroup"
        aria-label="Spice level"
        data-testid={`${testIdPrefix}-spice-level`}
      >
        {SPICE_OPTIONS.map((opt) => {
          const selected = (value || "") === opt.code;
          return (
            <button
              key={opt.code || "unset"}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`${testIdPrefix}-spice-${opt.code || "unset"}`}
              onClick={() => onChange(opt.code || null)}
              className={
                "h-11 min-w-[72px] px-3 rounded-lg border text-sm font-medium " +
                (selected
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
              }
            >
              {opt.short ? `${opt.short} · ${opt.label}` : opt.label}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500">
        The DEFAULT spice level for this dish. Customers see L / M / H on the
        product card, preset to it, and may change it before adding the item —
        their selection is what reaches the kitchen. Choose “Not set” for a
        dish with no spice selector at all.
      </p>
    </div>
  );
}

export default function ProductAttributesFields({
  value, onChange, config, vertical, testIdPrefix = "product",
}) {
  const [open, setOpen] = useState(false);
  const cfg = config || {};
  const showBrand = !!cfg.brand_enabled;
  const showPack = !!cfg.pack_enabled;
  const showFamily = !!cfg.family_enabled;
  const showDietary = cfg.dietary_enabled !== false;
  const showSpice = !!cfg.spice_enabled;
  const anyInDisclosure = showBrand || showPack || showFamily || showDietary;

  if (!anyInDisclosure && !showSpice) return null;

  const patch = (fields) => onChange({ ...value, ...fields });

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-attributes`}>
      {/* Spice sits outside the disclosure for restaurants — it is the
          one attribute a restaurant operator actually reaches for. */}
      {showSpice && (
        <SpiceLevelField
          value={value.spice_level}
          onChange={(code) => patch({ spice_level: code })}
          testIdPrefix={testIdPrefix}
        />
      )}

      {anyInDisclosure && (
        <div className="border border-slate-200 rounded-xl">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid={`${testIdPrefix}-attributes-toggle`}
            className="w-full min-h-[44px] px-4 py-2.5 flex items-center gap-2 text-left"
          >
            {open ? <ChevronDown className="w-4 h-4 text-slate-500" />
                  : <ChevronRight className="w-4 h-4 text-slate-500" />}
            <span className="text-sm font-medium text-slate-800">Product details (optional)</span>
            <span className="text-[11px] text-slate-500 ml-auto">
              {[showBrand && "Brand", showPack && "Size", showFamily && "Product family",
                showDietary && "Dietary"].filter(Boolean).join(" · ")}
            </span>
          </button>

          {open && (
            <div className="px-4 pb-4 grid sm:grid-cols-2 gap-3">
              {showBrand && (
                <AttributeCombobox
                  kind="brand"
                  label="Brand"
                  testId={`${testIdPrefix}-brand`}
                  valueId={value.brand_id}
                  valueLabel={value.brand}
                  onChange={(sel) => patch({ brand_id: sel.id, brand: sel.value })}
                  help="Who makes it. Add a new brand without leaving this form."
                />
              )}
              {showFamily && (
                <AttributeCombobox
                  kind="family"
                  label="Product family"
                  testId={`${testIdPrefix}-family`}
                  valueId={value.product_family_id}
                  valueLabel={value.product_family}
                  onChange={(sel) => patch({ product_family_id: sel.id, product_family: sel.value })}
                  help="The Sales Order tab this item appears under (e.g. Restaurant, Grocery, Vegetables, Meat). Leave blank to show it under All only."
                />
              )}
              {showPack && (
                <>
                  <div className="space-y-1">
                    <label
                      className="text-xs font-medium text-slate-700"
                      htmlFor={`${testIdPrefix}-package-size`}
                    >
                      Package size
                    </label>
                    <input
                      id={`${testIdPrefix}-package-size`}
                      data-testid={`${testIdPrefix}-package-size`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="12"
                      value={value.package_size_value ?? ""}
                      onChange={(e) => patch({
                        package_size_value: e.target.value === "" ? null : e.target.value,
                      })}
                      className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm"
                    />
                    <p className="text-[10px] text-slate-500">Needs a unit — "12" alone tells a customer nothing.</p>
                  </div>
                  <AttributeCombobox
                    kind="unit"
                    label="Unit of measure"
                    testId={`${testIdPrefix}-unit`}
                    valueId={value.unit_id}
                    valueLabel={value.unit_of_measure}
                    onChange={(sel) => patch({ unit_id: sel.id, unit_of_measure: sel.value })}
                    help="oz, kg, each, pack… Add your own if it is missing."
                  />
                  <div className="space-y-1 sm:col-span-2">
                    <label
                      className="text-xs font-medium text-slate-700"
                      htmlFor={`${testIdPrefix}-package-size-label`}
                    >
                      Named size <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      id={`${testIdPrefix}-package-size-label`}
                      data-testid={`${testIdPrefix}-package-size-label`}
                      type="text"
                      maxLength={40}
                      placeholder="Half Sheet, Family Pack…"
                      value={value.package_size_label ?? ""}
                      onChange={(e) => patch({ package_size_label: e.target.value || null })}
                      className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm"
                    />
                    <p className="text-[10px] text-slate-500">
                      For sizes that are words rather than numbers. Use Package size for a number.
                    </p>
                  </div>
                </>
              )}
              {showDietary && (
                <div className="sm:col-span-2 space-y-2">
                  <label className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`${testIdPrefix}-vegan`}
                      checked={!!value.vegan}
                      onChange={(e) => patch({ vegan: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <span className="text-sm text-slate-800">
                      Vegan
                      <span className="text-slate-500"> — shows a green “VEGAN” badge on the customer card</span>
                    </span>
                  </label>
                  <div className="space-y-1">
                    <label
                      className="text-xs font-medium text-slate-700"
                      htmlFor={`${testIdPrefix}-allergens`}
                    >
                      Allergens <span className="text-slate-400">(comma-separated)</span>
                    </label>
                    <input
                      id={`${testIdPrefix}-allergens`}
                      data-testid={`${testIdPrefix}-allergens`}
                      type="text"
                      placeholder="dairy, peanuts, gluten"
                      value={value.allergensText ?? ""}
                      onChange={(e) => patch({ allergensText: e.target.value })}
                      className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Turn the editor state into the product API payload. Kept beside the
// component so the two modals build an identical body, and so the
// dietary/allergen fields are sent as TYPED fields rather than the
// untyped `extra` blob the create route used to drop.
export function attributesToPayload(value, config) {
  const cfg = config || {};
  const payload = {};
  if (cfg.brand_enabled) {
    payload.brand_id = value.brand_id || null;
    payload.brand = value.brand || null;
  }
  if (cfg.family_enabled) {
    payload.product_family_id = value.product_family_id || null;
    payload.product_family = value.product_family || null;
  }
  if (cfg.pack_enabled) {
    payload.unit_id = value.unit_id || null;
    payload.unit_of_measure = value.unit_of_measure || null;
    payload.package_size_value =
      value.package_size_value === "" || value.package_size_value === null ||
      value.package_size_value === undefined
        ? null
        : Number(value.package_size_value);
    payload.package_size_label = value.package_size_label || null;
  }
  if (cfg.spice_enabled) {
    payload.spice_level = value.spice_level || null;
  }
  if (cfg.dietary_enabled !== false) {
    payload.vegan = !!value.vegan;
    payload.allergens = String(value.allergensText || "")
      .split(",").map((t) => t.trim()).filter(Boolean);
  }
  return payload;
}

// Seed the editor state from a persisted product (or an empty product
// for the create form).
export function attributesFromProduct(product) {
  const p = product || {};
  const dietary = Array.isArray(p.dietary) ? p.dietary : [];
  return {
    brand_id: p.brand_id || null,
    brand: p.brand || null,
    product_family_id: p.product_family_id || null,
    product_family: p.product_family || null,
    unit_id: p.unit_id || null,
    unit_of_measure: p.unit_of_measure || null,
    package_size_value: p.package_size_value ?? "",
    package_size_label: p.package_size_label || "",
    spice_level: p.spice_level || null,
    vegan: dietary.includes("vegan"),
    allergensText: (p.allergens || []).join(", "),
  };
}
